import { useState, useEffect, useRef, useCallback, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const MAX_REFRESH_RETRIES = 5;
const BASE_DELAY_MS = 1000;

function getRetryDelay(attempt: number): number {
  const base = BASE_DELAY_MS * Math.pow(2, attempt);
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.round(base + jitter);
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const sessionRef = useRef<Session | null>(null);

  const retrySessionRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    while (retryCountRef.current < MAX_REFRESH_RETRIES) {
      const attempt = retryCountRef.current;
      const delay = getRetryDelay(attempt);
      if (import.meta.env.DEV) console.log(`[Auth] Session refresh retry ${attempt + 1}/${MAX_REFRESH_RETRIES} in ${delay}ms`);

      await new Promise(resolve => {
        retryTimeoutRef.current = setTimeout(resolve, delay);
      });

      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session) {
          if (import.meta.env.DEV) console.log("[Auth] Session refresh succeeded");
          setSession(data.session);
          setUser(data.session.user);
          retryCountRef.current = 0;
          isRefreshingRef.current = false;
          return;
        }
        if (import.meta.env.DEV) console.warn(`[Auth] Refresh attempt ${attempt + 1} failed:`, error?.message);
      } catch (err) {
        if (import.meta.env.DEV) console.warn(`[Auth] Refresh attempt ${attempt + 1} network error:`, err);
      }

      retryCountRef.current++;
    }

    if (import.meta.env.DEV) console.error("[Auth] All refresh retries exhausted — session lost");
    retryCountRef.current = 0;
    isRefreshingRef.current = false;
    setSession(null);
    setUser(null);
  }, []);

  useEffect(() => {
    let initialSessionResolved = false;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      sessionRef.current = s;
      setUser(s?.user ?? null);
      initialSessionResolved = true;
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!initialSessionResolved && event === "INITIAL_SESSION") {
          return;
        }

        if (event === "SIGNED_OUT") {
          setSession(null);
          sessionRef.current = null;
          setUser(null);
          setLoading(false);
          return;
        }

        if (!newSession && sessionRef.current) {
          if (import.meta.env.DEV) console.warn("[Auth] Session lost unexpectedly, attempting recovery...");
          retrySessionRefresh();
          return;
        }

        setSession(newSession);
        sessionRef.current = newSession;
        setUser(newSession?.user ?? null);
        if (initialSessionResolved) {
          setLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [retrySessionRefresh]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
